/**
 * HistorySearchItems
 *
 * Sub-components used by QueryHistorySearchPage: FilterChip, HistoryResultItem.
 */

import {
  CheckCircle, XCircle, Clock, Database, Server,
} from 'lucide-react';
import { CommandItem } from '@/components/ui/command';
import { getQueryHistoryPreview } from '../utils/queryHistoryPreview';
import type { QueryHistoryEntry } from '../types/queryHistoryTypes';

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function formatDuration(ms?: number): string {
  if (ms == null) return '';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

type FilterChipProps = {
  label: string;
  active: boolean;
  onClick: () => void;
};

export function FilterChip({ label, active, onClick }: FilterChipProps) {
  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClick(); }}
      className={`px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors ${
        active
          ? 'bg-primary/10 border-primary/30 text-primary'
          : 'bg-muted/50 border-border/50 text-muted-foreground hover:bg-muted'
      }`}
    >
      {label}
    </button>
  );
}

type HistoryResultItemProps = {
  entry: QueryHistoryEntry;
  connectionName: string;
  onSelect: () => void;
};

export function HistoryResultItem({ entry, connectionName, onSelect }: HistoryResultItemProps) {
  const preview = getQueryHistoryPreview(entry.sql, 120);
  const duration = formatDuration(entry.executionTimeMs);
  const relTime = formatRelativeTime(
    entry.executedAt instanceof Date ? entry.executedAt : new Date(entry.executedAt),
  );

  return (
    <CommandItem
      value={`history:${entry.id}`}
      keywords={[entry.sql.slice(0, 200), connectionName, entry.database ?? '']}
      onSelect={onSelect}
      className="flex flex-col items-start gap-0.5 py-2"
      data-testid={`history-item-${entry.id}`}
    >
      {/* SQL preview */}
      <div className="flex items-start gap-1.5 w-full">
        <span className="flex-shrink-0 mt-0.5">
          {entry.success ? (
            <CheckCircle className="h-3 w-3 text-green-500/70" />
          ) : (
            <XCircle className="h-3 w-3 text-red-500/70" />
          )}
        </span>
        <span className="flex-1 min-w-0 text-xs font-mono leading-relaxed whitespace-pre-wrap break-words line-clamp-2">
          {preview}
        </span>
      </div>

      {/* Metadata row.
          PP-17: `text-muted-foreground` at full opacity, not `/70` — measured
          via canvas-composited computed colours against the actual popover
          background: `/70` renders at ~2.8:1 (light) / ~3.31:1 (dark),
          both below WCAG AA's 4.5:1 for this 10px text. Full opacity is the
          same token the group heading right above it already uses, and
          measures ~5.65:1 / ~5.93:1. */}
      <div className="flex items-center gap-2 ml-[18px] text-[10px] text-muted-foreground">
        <span className="flex items-center gap-0.5">
          <Server className="h-2.5 w-2.5" />
          {connectionName}
        </span>
        {entry.database && (
          <span className="flex items-center gap-0.5">
            <Database className="h-2.5 w-2.5" />
            {entry.database}
          </span>
        )}
        <span className="flex items-center gap-0.5">
          <Clock className="h-2.5 w-2.5" />
          {relTime}
        </span>
        {duration && <span>{duration}</span>}
        {entry.rowCount != null && entry.success && (
          <span>{entry.rowCount} rows</span>
        )}
      </div>
    </CommandItem>
  );
}
